import type {
  BigQueryImportKey,
  DatabricksImportKey,
  FabricImportKey,
  RedshiftImportKey,
  S3ImportKey,
  SnowflakeImportKey,
} from "@dashboardApi/apis/integrationsCdiImport";
import type { PartnerName } from "@technology-partners/cdi-import-sync-shared/shared/types";

export type CDISource =
  | SnowflakeSource
  | RedshiftSource
  | BigQuerySource
  | DatabricksSource
  | S3Source
  | FabricSource;

interface CommonSourceFields {
  id: string;
  name: string;
  app_group_id: string;
  data_warehouse_type: PartnerName;
  created_at: string;
  updated_at: string;
  query_timeout?: number;
  sync_count: number;
  segment_count: number;
  connection_status: ConnectionStatus;
}

export type ConnectionStatus = "connected" | "disconnected";

export interface SnowflakeSource extends CommonSourceFields {
  warehouse: string;
  database: string;
  schema: string;
  security_config?: SnowflakeImportKey;
}

export interface RedshiftSource extends CommonSourceFields {
  database: string;
  schema: string;
  security_config?: RedshiftImportKey;
}

export interface BigQuerySource extends CommonSourceFields {
  project: string;
  dataset: string;
  security_config?: BigQueryImportKey;
}

export interface DatabricksSource extends CommonSourceFields {
  catalog: string;
  schema: string;
  security_config?: DatabricksImportKey;
}

export interface FabricSource extends CommonSourceFields {
  database: string;
  schema: string;
  security_config?: FabricImportKey;
}

export interface S3Source extends CommonSourceFields {
  bucket: string;
  region: string;
  security_config?: S3ImportKey;
}

// Omit doesn't distribute over union types, create a distributive version
// https://stackoverflow.com/questions/57103834/omit-a-property-from-all-interfaces-in-a-union-but-keep-the-union-structure
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

// Exclude fields that are not required when creating a source
// app_group_id is set by the backend, so we don't need it when creating a new source
export type CreateCDISource = DistributiveOmit<
  CDISource,
  "id" | "app_group_id" | "created_at" | "updated_at" | "sync_count" | "segment_count"
> & {
  existing_creds_id?: string; // for reusing existing creds
};

export interface DeleteCDISourceResult {
  status?: string;
  message?: string;
}

export const isSnowflakeSource = (source: CDISource): source is SnowflakeSource =>
  source.data_warehouse_type === "snowflake";
export const isDatabricksSource = (source: CDISource): source is DatabricksSource =>
  source.data_warehouse_type === "databricks";
export const isRedshiftSource = (source: CDISource): source is RedshiftSource =>
  source.data_warehouse_type === "redshift";
export const isBigQuerySource = (source: CDISource): source is BigQuerySource =>
  source.data_warehouse_type === "bigquery";
export const isS3Source = (source: CDISource): source is S3Source => source.data_warehouse_type === "s3";
export const isFabricSource = (source: CDISource): source is FabricSource =>
  source.data_warehouse_type === "fabric";
